import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { MembershipProvider } from "@/contexts/MembershipContext";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import { ActiveChatProvider } from "@/contexts/ActiveChatContext";
import { ChatsProvider } from "@/contexts/ChatsContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { DisplayNameSetupDialog } from "./components/auth/DisplayNameSetupDialog";
import { Loader2 } from "lucide-react";

// Lazy-loaded page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Landing = lazy(() => import("./pages/Landing"));
const Collection = lazy(() => import("./pages/Collection"));
const Explore = lazy(() => import("./pages/Explore"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const Favorites = lazy(() => import("./pages/Favorites"));
const PublicCollection = lazy(() => import("./pages/PublicCollection"));
const Settings = lazy(() => import("./pages/Settings"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const About = lazy(() => import("./pages/About"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Communication = lazy(() => import("./pages/Communication"));
const AdminBugReports = lazy(() => import("./pages/AdminBugReports"));
const AdminChangeRequests = lazy(() => import("./pages/AdminChangeRequests"));
const CategoryLanding = lazy(() => import("./pages/CategoryLanding"));
const Categories = lazy(() => import("./pages/Categories"));

const queryClient = new QueryClient();

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function RootRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Landing />
      </Suspense>
    );
  }

  return (
    <ProtectedRoute requiredTier="creator">
      <AppLayout />
    </ProtectedRoute>
  );
}

/**
 * Wrapper that renders the page inside AppLayout when authenticated,
 * or standalone when the user is a guest.
 */
function AuthAwarePage({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (user) {
    return <AppLayout />;
  }

  return <><div className="min-h-screen bg-background">{children}</div><SiteFooter /></>;
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

const router = createBrowserRouter([
  // Public routes
  { path: "/login", element: <SuspenseWrapper><Login /></SuspenseWrapper> },
  { path: "/register", element: <SuspenseWrapper><Register /></SuspenseWrapper> },
  {
    path: "/about",
    element: <AuthAwarePage><SuspenseWrapper><About /></SuspenseWrapper></AuthAwarePage>,
    children: [{ index: true, element: <SuspenseWrapper><About /></SuspenseWrapper> }],
  },
  {
    path: "/faq",
    element: <AuthAwarePage><SuspenseWrapper><FAQ /></SuspenseWrapper></AuthAwarePage>,
    children: [{ index: true, element: <SuspenseWrapper><FAQ /></SuspenseWrapper> }],
  },
  { path: "/verify-email", element: <SuspenseWrapper><VerifyEmail /></SuspenseWrapper> },
  { path: "/forgot-password", element: <SuspenseWrapper><ForgotPassword /></SuspenseWrapper> },
  { path: "/reset-password", element: <SuspenseWrapper><ResetPassword /></SuspenseWrapper> },

  // Root: Landing for guests, Dashboard for authenticated
  {
    path: "/",
    element: <RootRoute />,
    children: [{ index: true, element: <SuspenseWrapper><Index /></SuspenseWrapper> }],
  },

  // Public routes with auth-aware layout (accessible without login)
  {
    path: "/explore",
    element: <AuthAwarePage><SuspenseWrapper><Explore /></SuspenseWrapper></AuthAwarePage>,
    children: [{ index: true, element: <SuspenseWrapper><Explore /></SuspenseWrapper> }],
  },
  {
    path: "/explore/:id",
    element: <AuthAwarePage><SuspenseWrapper><PublicCollection /></SuspenseWrapper></AuthAwarePage>,
    children: [{ index: true, element: <SuspenseWrapper><PublicCollection /></SuspenseWrapper> }],
  },

  // Categories index page (public)
  {
    path: "/categories",
    element: <AuthAwarePage><SuspenseWrapper><Categories /></SuspenseWrapper></AuthAwarePage>,
    children: [{ index: true, element: <SuspenseWrapper><Categories /></SuspenseWrapper> }],
  },

  // Category landing pages (public, SEO-driven)
  {
    path: "/collections/:slug",
    element: <AuthAwarePage><SuspenseWrapper><CategoryLanding /></SuspenseWrapper></AuthAwarePage>,
    children: [{ index: true, element: <SuspenseWrapper><CategoryLanding /></SuspenseWrapper> }],
  },

  // Protected routes with shared layout
  {
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "/collection/:id",
        element: (
          <ProtectedRoute requiredTier="creator">
            <SuspenseWrapper><Collection /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      { path: "/marketplace", element: <SuspenseWrapper><Marketplace /></SuspenseWrapper> },
      { path: "/favorites", element: <SuspenseWrapper><Favorites /></SuspenseWrapper> },
      { path: "/communication", element: <SuspenseWrapper><Communication /></SuspenseWrapper> },
      { path: "/settings", element: <SuspenseWrapper><Settings /></SuspenseWrapper> },
      { path: "/admin/bugs", element: <SuspenseWrapper><AdminBugReports /></SuspenseWrapper> },
      { path: "/admin/changes", element: <SuspenseWrapper><AdminChangeRequests /></SuspenseWrapper> },
    ],
  },

  // Redirects for old routes
  { path: "/notifications", element: <Navigate to="/communication?tab=notifications" replace /> },
  { path: "/chat", element: <Navigate to="/communication?tab=chats" replace /> },

  // Catch-all
  { path: "*", element: <SuspenseWrapper><NotFound /></SuspenseWrapper> },
]);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <MembershipProvider>
        <ActiveChatProvider>
          <NotificationsProvider>
            <ChatsProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <DisplayNameSetupDialog />
                <RouterProvider router={router} />
              </TooltipProvider>
            </ChatsProvider>
          </NotificationsProvider>
        </ActiveChatProvider>
      </MembershipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
